import React, { useState, useEffect } from "react";
import { X, Plus, Trash2, Calendar, Clock, Zap, Tag, DollarSign, Package, Search, AlertCircle } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { useTheme } from "components/Theme/ThemeProvider";
import { summaryApi } from "common";
import { authFetch } from "../../../helpers/authFetch";



function CreateDeal({ onClose, fetchDeals }) {
  const { colors } = useTheme();
  const [step, setStep] = useState(1);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [allProducts, setAllProducts] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedProducts, setSelectedProducts] = useState([]);

  const [dealData, setDealData] = useState({
    dealName: "",
    dealDescription: "",
    dealType: "flash_sale",
    startDate: "",
    endDate: "",
    banner: "",
    priority: 0
  });

  useEffect(() => {
    fetchAllProducts();
  }, []);

  const fetchAllProducts = async () => {
    try {
      const response = await fetch(summaryApi.allProducts.url, {
        method: summaryApi.allProducts.method,
        });
      const data = await response.json();
      if (data.success) {
        setAllProducts(data.data || []);
      }
    } catch (error) {
      console.error("Error fetching products:", error);
    }
  };

  const handleDealDataChange = (e) => {
    const { name, value } = e.target;
    setDealData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddProduct = (product) => {
    if (selectedProducts.find(p => p.productId === product._id)) {
      return;
    }

    setSelectedProducts(prev => [...prev, {
      productId: product._id,
      productName: product.productName,
      productImage: product.productImage[0],
      originalPrice: product.productPrice,
      dealPrice: product.productPrice,
      dealDiscount: 0,
      stockLimit: null
    }]);
  };

  const handleRemoveProduct = (productId) => {
    setSelectedProducts(prev => prev.filter(p => p.productId !== productId));
  };

  const handleProductChange = (productId, field, value) => {
    setSelectedProducts(prev => prev.map(p => {
      if (p.productId === productId) {
        const updated = { ...p, [field]: value };
        
        if (field === "dealDiscount") {
          const discount = parseFloat(value) || 0;
          updated.dealPrice = p.originalPrice - discount;
        } else if (field === "dealPrice") {
          const price = parseFloat(value) || 0;
          updated.dealDiscount = p.originalPrice - price;
        }
        
        return updated;
      }
      return p;
    }));
  };

  const handleSubmit = async () => {
    if (!dealData.dealName || !dealData.dealDescription || selectedProducts.length === 0) {
      alert("Please fill all required fields and add at least one product");
      return;
    }

    if (!dealData.startDate || !dealData.endDate) {
      alert("Please select start and end dates");
      return;
    }

    try {
      setIsSubmitting(true);
      
      const submitData = {
        ...dealData,
        products: selectedProducts.map(p => ({
          productId: p.productId,
          dealPrice: parseFloat(p.dealPrice),
          dealDiscount: parseFloat(p.dealDiscount),
          stockLimit: p.stockLimit ? parseInt(p.stockLimit) : null
        }))
      };

      const response = await authFetch(summaryApi.createDeal.url, {
        method: summaryApi.createDeal.method,        
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(submitData)
      });

      const data = await response.json();

      if (data.success) {
        alert("Deal created successfully!");
        fetchDeals();
        onClose();
      } else {
        alert(data.message || "Failed to create deal");
      }
    } catch (error) {
      console.error("Error creating deal:", error);
      alert("Failed to create deal");
    } finally {
      setIsSubmitting(false);
    }
  };

  const filteredProducts = allProducts.filter(product =>
    product.productName.toLowerCase().includes(searchQuery.toLowerCase()) ||
    product.productBrand.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4 overflow-y-auto"
      style={{ backgroundColor: "rgba(0, 0, 0, 0.8)" }}
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.9, y: 20 }}
        animate={{ scale: 1, y: 0 }}
        exit={{ scale: 0.9, y: 20 }}
        className="rounded-2xl shadow-2xl w-full max-w-6xl transform"
        style={{
          backgroundColor: colors.bg,
          border: `1px solid ${colors.border}`
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between p-6 border-b"
          style={{ borderColor: colors.border }}
        >
          <div className="flex items-center gap-3">
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
              className="p-3 rounded-xl"
              style={{ backgroundColor: `${colors.primary}20` }}
            >
              <Zap className="w-6 h-6" style={{ color: colors.primary }} />
            </motion.div>
            <div>
              <h2 className="text-2xl font-bold" style={{ color: colors.text }}>
                Create Flash Deal
              </h2>
              <p className="text-sm" style={{ color: colors.textSecondary }}>
                Step {step} of 2
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg transition-all hover:scale-110"
            style={{ backgroundColor: colors.bgSecondary, color: colors.text }}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Progress Bar */}
        <div className="px-6 pt-4">
          <div className="flex items-center gap-2">
            {[1, 2].map((s) => (
              <div
                key={s}
                className="flex-1 h-2 rounded-full transition-all duration-300"
                style={{
                  backgroundColor: step >= s ? colors.primary : colors.border
                }}
              />
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="p-6 max-h-[calc(100vh-250px)] overflow-y-auto">
          <AnimatePresence mode="wait">
            {step === 1 ? (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-6"
              >
                {/* Deal Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                      Deal Name *
                    </label>
                    <input
                      type="text"
                      name="dealName"
                      value={dealData.dealName}
                      onChange={handleDealDataChange}
                      placeholder="e.g., Black Friday Mega Sale"
                      className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                      style={{
                        backgroundColor: colors.bgSecondary,
                        color: colors.text,
                        border: `2px solid ${colors.border}`
                      }}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                      Deal Description *
                    </label>
                    <textarea
                      name="dealDescription"
                      value={dealData.dealDescription}
                      onChange={handleDealDataChange}
                      placeholder="Describe your amazing deal..."
                      rows={3}
                      className="w-full px-4 py-3 rounded-lg outline-none transition-all resize-none"
                      style={{
                        backgroundColor: colors.bgSecondary,
                        color: colors.text,
                        border: `2px solid ${colors.border}`
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                      Deal Type
                    </label>
                    <select
                      name="dealType"
                      value={dealData.dealType}
                      onChange={handleDealDataChange}
                      className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                      style={{
                        backgroundColor: colors.bgSecondary,
                        color: colors.text,
                        border: `2px solid ${colors.border}`
                      }}
                    >
                      <option value="flash_sale">⚡ Flash Sale</option>
                      <option value="limited_time">⏰ Limited Time</option>
                      <option value="clearance">🔥 Clearance</option>
                      <option value="special_offer">🎁 Special Offer</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                      Priority (Higher shows first)
                    </label>
                    <input
                      type="number"
                      name="priority"
                      value={dealData.priority}
                      onChange={handleDealDataChange}
                      placeholder="0"
                      className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                      style={{
                        backgroundColor: colors.bgSecondary,
                        color: colors.text,
                        border: `2px solid ${colors.border}`
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 items-center gap-2" style={{ color: colors.text }}>
                      <Calendar className="w-4 h-4" />
                      Start Date *
                    </label>
                    <input
                      type="datetime-local"
                      name="startDate"
                      value={dealData.startDate}
                      onChange={handleDealDataChange}
                      className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                      style={{
                        backgroundColor: colors.bgSecondary,
                        color: colors.text,
                        border: `2px solid ${colors.border}`
                      }}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-semibold mb-2 items-center gap-2" style={{ color: colors.text }}>
                      <Clock className="w-4 h-4" />
                      End Date *
                    </label>
                    <input
                      type="datetime-local"
                      name="endDate"
                      value={dealData.endDate}
                      onChange={handleDealDataChange}
                      className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                      style={{
                        backgroundColor: colors.bgSecondary,
                        color: colors.text,
                        border: `2px solid ${colors.border}`
                      }}
                    />
                  </div>

                  <div className="md:col-span-2">
                    <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                      Banner Image URL (Optional)
                    </label>
                    <input
                      type="text"
                      name="banner"
                      value={dealData.banner}
                      onChange={handleDealDataChange}
                      placeholder="https://example.com/banner.jpg"
                      className="w-full px-4 py-3 rounded-lg outline-none transition-all"
                      style={{
                        backgroundColor: colors.bgSecondary,
                        color: colors.text,
                        border: `2px solid ${colors.border}`
                      }}
                    />
                  </div>
                </div>
              </motion.div>
            ) : (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                {/* Search Products */}
                <div>
                  <label className="block text-sm font-semibold mb-2" style={{ color: colors.text }}>
                    Search & Add Products *
                  </label>
                  <div className="relative">
                    <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5" style={{ color: colors.textSecondary }} />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      placeholder="Search products by name or brand..."
                      className="w-full pl-12 pr-4 py-3 rounded-lg outline-none transition-all"
                      style={{
                        backgroundColor: colors.bgSecondary,
                        color: colors.text,
                        border: `2px solid ${colors.border}`
                      }}
                    />
                  </div>
                </div>

                {/* Products Grid */}
                <div
                  className="grid grid-cols-2 md:grid-cols-4 gap-4 max-h-64 overflow-y-auto p-4 rounded-lg"
                  style={{ backgroundColor: colors.bgSecondary }}
                >
                  {filteredProducts.map((product) => (
                    <motion.div
                      key={product._id}
                      whileHover={{ scale: 1.05 }}
                      className="relative cursor-pointer rounded-lg overflow-hidden"
                      style={{ border: `2px solid ${colors.border}` }}
                      onClick={() => handleAddProduct(product)}
                    >
                      <img
                        src={product.productImage[0]}
                        alt={product.productName}
                        className="w-full h-32 object-cover"
                      />
                      <div className="p-2" style={{ backgroundColor: colors.bg }}>
                        <p className="text-xs font-semibold truncate" style={{ color: colors.text }}>
                          {product.productName}
                        </p>
                        <p className="text-xs" style={{ color: colors.primary }}>
                          ${product.productPrice}
                        </p>
                      </div>
                    </motion.div>
                  ))}
                </div>

                {/* Selected Products */}
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <label className="text-sm font-semibold" style={{ color: colors.text }}>
                      Selected Products ({selectedProducts.length})
                    </label>
                    {selectedProducts.length === 0 && (
                      <div className="flex items-center gap-2 text-sm" style={{ color: colors.error }}>
                        <AlertCircle className="w-4 h-4" />
                        Add at least one product
                      </div>
                    )}
                  </div>

                  <div className="space-y-4">
                    {selectedProducts.map((product) => (
                      <motion.div
                        key={product.productId}
                        initial={{ opacity: 0, y: 10 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -10 }}
                        className="p-4 rounded-lg"
                        style={{
                          backgroundColor: colors.bgSecondary,
                          border: `1px solid ${colors.border}`
                        }}
                      >
                        <div className="flex gap-4">
                          <img
                            src={product.productImage}
                            alt={product.productName}
                            className="w-20 h-20 object-cover rounded-lg"
                          />
                          <div className="flex-1 grid grid-cols-1 md:grid-cols-4 gap-4">
                            <div>
                              <p className="text-sm font-semibold mb-1" style={{ color: colors.text }}>
                                {product.productName}
                              </p>
                              <p className="text-xs" style={{ color: colors.textSecondary }}>
                                Original: ${product.originalPrice}
                              </p>
                            </div>

                            <div>
                              <label className="text-xs" style={{ color: colors.textSecondary }}>Deal Price</label>
                              <input
                                type="number"
                                value={product.dealPrice}
                                onChange={(e) => handleProductChange(product.productId, "dealPrice", e.target.value)}
                                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                                style={{
                                  backgroundColor: colors.bg,
                                  color: colors.text,
                                  border: `1px solid ${colors.border}`
                                }}
                              />
                            </div>

                            <div>
                              <label className="text-xs" style={{ color: colors.textSecondary }}>Discount Amount</label>
                              <input
                                type="number"
                                value={product.dealDiscount}
                                onChange={(e) => handleProductChange(product.productId, "dealDiscount", e.target.value)}
                                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                                style={{
                                  backgroundColor: colors.bg,
                                  color: colors.text,
                                  border: `1px solid ${colors.border}`
                                }}
                              />
                            </div>

                            <div>
                              <label className="text-xs" style={{ color: colors.textSecondary }}>Stock Limit</label>
                              <input
                                type="number"
                                value={product.stockLimit || ""}
                                onChange={(e) => handleProductChange(product.productId, "stockLimit", e.target.value)}
                                placeholder="Unlimited"
                                className="w-full px-3 py-2 rounded-lg text-sm outline-none"
                                style={{
                                  backgroundColor: colors.bg,
                                  color: colors.text,
                                  border: `1px solid ${colors.border}`
                                }}
                              />
                            </div>
                          </div>

                          <button
                            onClick={() => handleRemoveProduct(product.productId)}
                            className="p-2 rounded-lg h-fit transition-all hover:scale-110"
                            style={{ backgroundColor: `${colors.error}20`, color: colors.error }}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between p-6 border-t"
          style={{ borderColor: colors.border }}
        >
          <button
            onClick={() => step === 1 ? onClose() : setStep(1)}
            className="px-6 py-3 font-semibold rounded-lg transition-all hover:scale-105"
            style={{
              backgroundColor: colors.bgSecondary,
              color: colors.text,
              border: `1px solid ${colors.border}`
            }}
          >
            {step === 1 ? "Cancel" : "Back"}
          </button>

          {step === 1 ? (
            <button
              onClick={() => setStep(2)}
              disabled={!dealData.dealName || !dealData.dealDescription || !dealData.startDate || !dealData.endDate}
              className="px-6 py-3 font-semibold text-white rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
              }}
            >
              Next: Add Products
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={isSubmitting || selectedProducts.length === 0}
              className="px-6 py-3 font-semibold text-white rounded-lg transition-all hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{
                background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.accent} 100%)`
              }}
            >
              {isSubmitting ? "Creating..." : "Create Deal"}
            </button>
          )}
        </div>
      </motion.div>
    </motion.div>
  );
}

export default CreateDeal;